import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { getBootstrap } from "@/lib/api/bootstrap";
import { getNotifications } from "@/lib/api/notifications";
import { ApiError } from "@/lib/api/client";
import { letzteMitgliedssitzung, merkeSitzung } from "@/lib/session/offlineSession";
import { reportClientError } from "@/lib/monitoring";
import type { AdminBootstrap, BootstrapResponse, CustomerBootstrap } from "@/lib/types/session";
import type { Advisor, AuditLogEntry, NotificationEntry } from "@/lib/types/session";
import type { Client } from "@/lib/types/client";

/** Leerer Bootstrap-Snapshot — Ausgangszustand und Ziel jedes Logouts. */
export const ANONYMOUS_BOOTSTRAP: BootstrapResponse = { session: null, clients: [], advisors: [], logs: [], notifications: [] };

export type SessionStatus = "loading" | "ready";

interface SessionContextValue {
  status: SessionStatus;
  session: BootstrapResponse["session"];
  clients: Client[];
  advisors: Advisor[];
  logs: AuditLogEntry[];
  notifications: NotificationEntry[];
  /** Ersetzt den kompletten Bootstrap-Snapshot (nach Login/Aktivierung/Impersonation/Logout). */
  applyBootstrap: (data: BootstrapResponse) => void;
  /** Lädt GET /api/bootstrap erneut (z. B. nach reactivate, das keinen frischen Client zurückgibt). */
  refetch: () => Promise<void>;
  /** Admin: Client-Liste transformieren (anlegen/löschen/ersetzen). Kunde: Array bleibt ein Einzeltupel. */
  setClients: (updater: (clients: Client[]) => Client[]) => void;
  /** Einen einzelnen Client per Updater-Funktion ersetzen (z. B. nach saveClient). */
  updateClient: (clientId: string, updater: (client: Client) => Client) => void;
  setAdvisors: (updater: (advisors: Advisor[]) => Advisor[]) => void;
  setLogs: (updater: (logs: AuditLogEntry[]) => AuditLogEntry[]) => void;
  setNotifications: (updater: (notifications: NotificationEntry[]) => NotificationEntry[]) => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>("loading");
  const [bootstrap, setBootstrapState] = useState<BootstrapResponse>(ANONYMOUS_BOOTSTRAP);

  const load = useCallback(async () => {
    try {
      const data = await getBootstrap();
      setBootstrapState(data);
      merkeSitzung(data.session);
    } catch (error) {
      // **Kein Netz ist keine Abmeldung** (Nachtrag zu S4.3). Bis hierher fiel
      // jeder Fehler in denselben Zweig, und ein Fahrer im Funkloch landete auf
      // dem Login — wo er sich ohne Netz nicht anmelden kann. Die gesamte
      // Offline-Schicht dahinter war damit unerreichbar.
      //
      // Unterschieden wird wie in der Warteschlange (offline/outbox.ts):
      // ein `ApiError` ist eine *Antwort* des Servers und wird geglaubt, ein
      // `TypeError` von `fetch` heißt, dass die Frage nie ankam. Nur im zweiten
      // Fall gilt weiter, was zuletzt galt.
      const gemerkt = error instanceof ApiError ? null : letzteMitgliedssitzung();
      if (gemerkt) {
        // Die vier Listen bleiben leer, und zwar für immer: sie tragen
        // Kundendaten des Büros, und die sind offline weder vorhanden noch
        // erwünscht. Ein Mitglied braucht sie ohnehin nicht — sein Tag kommt
        // aus dem Gerätespeicher (features/mobile/offline/dayStore.ts).
        setBootstrapState({ session: gemerkt, clients: [], advisors: [], logs: [], notifications: [] });
      } else {
        reportClientError(error, { source: "bootstrap" });
        setBootstrapState(ANONYMOUS_BOOTSTRAP);
      }
    } finally {
      setStatus("ready");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Die eine Stelle, an der der Zustand von außen ersetzt wird — Anmeldung,
   * Abmeldung, Impersonation. Der Offline-Hinweis wird deshalb hier
   * mitgeschrieben und nicht an jedem Aufrufer: eine Abmeldung, die ihn stehen
   * ließe, zeigte dem nächsten Menschen am selben Gerät den Tagesbestand des
   * vorigen.
   */
  const applyBootstrap = useCallback((data: BootstrapResponse) => {
    setBootstrapState(data);
    merkeSitzung(data.session);
  }, []);

  const setClients = useCallback((updater: (clients: Client[]) => Client[]) => {
    setBootstrapState((prev) => {
      if (prev.session === null) return prev;
      const nextClients = updater(prev.clients as Client[]);
      if (prev.session.actorType === "admin") {
        return { ...prev, clients: nextClients } as AdminBootstrap;
      }
      if (prev.session.actorType !== "customer") return prev;
      const next = nextClients[0];
      if (!next) return prev;
      return { ...prev, clients: [next] } as CustomerBootstrap;
    });
  }, []);

  const updateClient = useCallback(
    (clientId: string, updater: (client: Client) => Client) => {
      setClients((clients) => clients.map((client) => (client.id === clientId ? updater(client) : client)));
    },
    [setClients]
  );

  const setAdvisors = useCallback((updater: (advisors: Advisor[]) => Advisor[]) => {
    setBootstrapState((prev): BootstrapResponse => {
      if (prev.session === null) return prev;
      return { ...prev, advisors: updater(prev.advisors) } as BootstrapResponse;
    });
  }, []);

  const setLogs = useCallback((updater: (logs: AuditLogEntry[]) => AuditLogEntry[]) => {
    setBootstrapState((prev): BootstrapResponse => {
      if (!prev.session || prev.session.actorType !== "admin") return prev;
      return { ...prev, logs: updater(prev.logs) } as AdminBootstrap;
    });
  }, []);

  const setNotifications = useCallback((updater: (notifications: NotificationEntry[]) => NotificationEntry[]) => {
    setBootstrapState((prev): BootstrapResponse => {
      if (!prev.session || prev.session.actorType !== "admin") return prev;
      return { ...prev, notifications: updater(prev.notifications) } as AdminBootstrap;
    });
  }, []);

  const isAdmin = bootstrap.session?.actorType === "admin";
  useEffect(() => {
    if (!isAdmin) return;
    const interval = setInterval(() => {
      getNotifications()
        .then((notifications) => setNotifications(() => notifications))
        .catch((error) => reportClientError(error, { source: "notifications-poll" }));
    }, 30000);
    return () => clearInterval(interval);
  }, [isAdmin, setNotifications]);

  const value = useMemo<SessionContextValue>(
    () => ({
      status,
      session: bootstrap.session,
      clients: bootstrap.clients,
      advisors: bootstrap.advisors,
      logs: bootstrap.logs,
      notifications: bootstrap.notifications,
      applyBootstrap,
      refetch: load,
      setClients,
      updateClient,
      setAdvisors,
      setLogs,
      setNotifications
    }),
    [status, bootstrap, applyBootstrap, load, setClients, updateClient, setAdvisors, setLogs, setNotifications]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession() muss innerhalb von <SessionProvider> aufgerufen werden.");
  }
  return context;
}
